/**
 * Die Zugreihenfolge: wer beginnt, wer als nächstes zieht, wer übersprungen wird.
 *
 * §9 macht zwei Vorgaben. Erstens ist immer genau **ein** Spieler aktiv. Zweitens sollen in
 * Teamspielen Mitglieder desselben Teams **nicht direkt hintereinander** ziehen, mit zwei
 * Mustern als Beispiel:
 *
 * ```
 * 3:3     A1  B1  A2  B2  A3  B3
 * 2:2:2   A1  B1  C1  A2  B2  C2
 * ```
 *
 * Die zweite Vorgabe ist ein **Minimierungsgebot, kein absolutes Verbot**
 * (`docs/OPEN_QUESTIONS.md` Nr. 24): bei ungleichen Teams ist eine Nachbarschaft geometrisch
 * unvermeidbar, und ungleiche Teams sind ausdrücklich erlaubt. Wie viele unvermeidbar sind,
 * sagt `minimalTeamAdjacencies`.
 *
 * **Erzeugen und Prüfen sind getrennt**, weil §9 beides verlangt („serverseitig erzeugt und
 * validiert"): `createTurnOrder` erzeugt, `createGameState` prüft. Ein Test hält fest, dass
 * der Prüfer jede erzeugte Reihenfolge annimmt — liefen die beiden auseinander, wäre eines
 * von ihnen falsch.
 *
 * **Die erzeugte Reihenfolge ist danach konstant** (`docs/OPEN_QUESTIONS.md` Nr. 23).
 * Scheidet ein Spieler aus, wird er übersprungen und die Reihenfolge bleibt stehen — auch
 * wenn dadurch zwei Teammitglieder benachbart werden. §9 gilt für die Erzeugung, nicht
 * dauerhaft. Ausscheiden kann ohnehin nur in Variante B passieren (Nr. 22).
 */
import { activePlayers, isEliminated, setStatus } from './game-state.js';
import { syncClock } from './game-clock.js';
function groupByTeam(players) {
    const groups = [];
    const byKey = new Map();
    // Reihenfolge der Gruppen = Reihenfolge ihres ersten Auftretens in der Spielerliste.
    // Das macht die Erzeugung deterministisch und damit Partien reproduzierbar (§128).
    for (const player of players) {
        const key = player.team ?? player.id;
        let group = byKey.get(key);
        if (group === undefined) {
            group = { key, members: [] };
            byKey.set(key, group);
            groups.push(group);
        }
        group.members.push(player.id);
    }
    return groups;
}
/**
 * Die Gruppe mit den meisten offenen Mitgliedern, die keinen der verbotenen Schlüssel trägt.
 *
 * Bei Gleichstand gewinnt die Gruppe, die in der Spielerliste zuerst auftrat — das macht die
 * Erzeugung deterministisch.
 */
function largestGroup(groups, forbidden) {
    let chosen;
    for (const group of groups) {
        if (group.members.length === 0)
            continue;
        if (forbidden.includes(group.key))
            continue;
        if (chosen === undefined || group.members.length > chosen.members.length) {
            chosen = group;
        }
    }
    return chosen;
}
/**
 * Erzeugt die Zugreihenfolge für diese Spieler.
 *
 * Ohne Teams bleibt die übergebene Spielerreihenfolge **unverändert**: §9 stellt dann keine
 * Bedingung, und jede Umsortierung wäre eine erfundene Regel. Wer anfängt, entscheidet damit
 * der Host über die Reihenfolge, in der er die Spieler übergibt (§42).
 *
 * Mit Teams wird verschränkt: für jeden Platz kommt die Gruppe mit den **meisten noch
 * offenen** Mitgliedern zum Zug, nie dieselbe wie beim Platz davor — und auf dem letzten
 * Platz auch nicht die des ersten, weil die Reihenfolge zyklisch ist. Bei Gleichstand gilt
 * die Reihenfolge des ersten Auftretens. Für die beiden Muster aus §9 ergibt das genau die
 * dort genannten Reihenfolgen.
 *
 * **Ungleiche Teams sind erlaubt** (`docs/OPEN_QUESTIONS.md` Nr. 24): zwei Anfänger gegen
 * einen erfahrenen Spieler funktioniert am physischen Brett und soll hier ebenso gehen. Geht
 * eine Aufteilung nicht vollständig auf, gibt das Verfahren so spät wie möglich nach — erst
 * wird die zyklische Bedingung gelockert, dann die Nachbarschaft zum Vorgänger. Damit bleibt
 * die Zahl der Doppelzüge auf dem Minimum, das `minimalTeamAdjacencies` nennt, und
 * `createGameState` prüft genau dieses Minimum nach.
 */
export function createTurnOrder(players) {
    if (players.length === 0) {
        // Die leere Reihenfolge, nicht ein Fehler: eine Lobby darf vorübergehend ohne Zuordnung
        // sein (Nr. 31), und `createGameState` lässt genau das vor dem Start zu. Bis Zyklus 8f
        // stand hier ein Wurf als Wächter gegen „die Spieler vergessen" — dafür gibt es jetzt
        // einen berechtigten Aufrufer.
        return [];
    }
    if (players.every((player) => player.team === undefined)) {
        return players.map((player) => player.id);
    }
    const groups = groupByTeam(players);
    const order = [];
    let previousKey;
    let firstKey;
    for (let slot = 0; slot < players.length; slot++) {
        const isLastSlot = slot === players.length - 1;
        // Der Reihe nach immer weniger streng: zuerst beides meiden, dann nur den Vorgänger,
        // zuletzt nehmen, was übrig ist. So entsteht eine Nachbarschaft erst, wenn sie
        // unvermeidbar ist — und dann nur eine.
        const chosen = largestGroup(groups, isLastSlot ? [previousKey, firstKey] : [previousKey]) ??
            largestGroup(groups, [previousKey]) ??
            largestGroup(groups, []);
        if (chosen === undefined) {
            // Unerreichbar: es sind noch Plätze frei, also hat eine Gruppe noch Mitglieder.
            throw new Error('Die Zugreihenfolge ließ sich nicht vollständig besetzen');
        }
        order.push(chosen.members.shift());
        previousKey = chosen.key;
        firstKey ??= chosen.key;
    }
    return order;
}
/**
 * Wer nach dem aktiven Spieler zieht — Ausgeschiedene übersprungen.
 *
 * Ist noch niemand aktiv, ist der erste nicht ausgeschiedene Spieler der Reihenfolge dran.
 * Ist niemand mehr übrig, gibt es keinen nächsten (`undefined`); in Variante B endet die
 * Partie dann, was die Siegbedingung entscheidet und nicht diese Funktion.
 *
 * Der aktive Spieler darf selbst schon ausgeschieden sein — beim Zeitablauf trifft es genau
 * ihn. Gesucht wird ab dem Platz **nach** ihm, er kommt also nicht erneut dran, solange ein
 * anderer übrig ist.
 */
export function getNextPlayer(state) {
    const remaining = activePlayers(state);
    if (remaining.length === 0)
        return undefined;
    if (state.activePlayer === undefined)
        return remaining[0];
    const index = state.turnOrder.indexOf(state.activePlayer);
    if (index === -1) {
        throw new Error(`Der aktive Spieler '${state.activePlayer}' steht nicht in der Zugreihenfolge`);
    }
    for (let step = 1; step <= state.turnOrder.length; step++) {
        const candidate = state.turnOrder[(index + step) % state.turnOrder.length];
        if (!isEliminated(state, candidate))
            return candidate;
    }
    // Unerreichbar: `remaining` ist nicht leer, also findet der Rundgang jemanden.
    return undefined;
}
/**
 * Beginnt die Partie: SETUP → PLAYING, der erste Spieler der Reihenfolge ist am Zug.
 *
 * Bewusst **nicht** aus `PAUSED` erreichbar, obwohl §47 von dort nach `PLAYING` zurückführt —
 * eine Fortsetzung darf die Zugnummer nicht auf 1 zurücksetzen. Pausieren und Fortsetzen
 * läuft über `setStatus`.
 */
export function startPlaying(state, now) {
    if (state.status !== 'SETUP') {
        throw new Error(`Eine Partie beginnt aus SETUP, nicht aus ${state.status} (§47)`);
    }
    const first = getNextPlayer({ ...state, activePlayer: undefined });
    if (first === undefined) {
        throw new Error('Kein Spieler kann die Partie beginnen');
    }
    // `syncClock` wirft eine Zeitpartie ohne `now` zurück — die Uhr startet mit dem ersten Zug.
    return syncClock({ ...setStatus(state, 'PLAYING'), activePlayer: first, turnNumber: 1 }, now);
}
/**
 * Beendet den Zug und übergibt an den nächsten Spieler.
 *
 * Nur im Status `PLAYING` — in `PAUSED` läuft nichts, und vor dem Spielbeginn gibt es keinen
 * Zug zu beenden. Bleibt niemand übrig, ist danach niemand am Zug; das ist kein Fehler,
 * sondern der Zustand, in dem die Siegbedingung greift.
 */
export function advanceTurn(state, now) {
    if (state.status !== 'PLAYING') {
        throw new Error(`Ein Zugwechsel ist nur im Status PLAYING möglich, nicht in ${state.status}`);
    }
    // Mit der Übergabe des Zugrechts wandert die Uhr — beides ist dieselbe Tatsache (§38).
    return syncClock({ ...state, activePlayer: getNextPlayer(state), turnNumber: state.turnNumber + 1 }, now);
}
/** Ist dieser Spieler am Zug? Solange niemand am Zug ist, ist es niemand. */
export function isActivePlayer(state, id) {
    return state.activePlayer === id;
}
//# sourceMappingURL=turn-engine.js.map